import { useState, useCallback, useEffect, useMemo } from 'react';
import { buildQueryParams } from './useDashboardDataUtils';
import { useCoreData, useSecondaryData } from './useDashboardDataHooks';

export const useDashboardData = (
  timePeriod,
  customRange = null,
  templateGridPage = 1,
  templateGridPageSize = 10,
  templateGridCategory = null,
  templateGridHideZeroActivity = false,
  templateGridSearch = '',
  templateGridSortBy = 'last_activity',
  templateGridSortOrder = 'desc'
) => {
  // Overall loading state for UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine if we're using all-time (MV stats) or time-filtered (legacy)
  const isAllTime = useMemo(() => {
    return timePeriod === 'all-time' && !customRange;
  }, [timePeriod, customRange]);

  // Build query parameters based on timePeriod or customRange
  const { queryString, requestKeySuffix } = useMemo(() => {
    return buildQueryParams(timePeriod, customRange, isAllTime);
  }, [timePeriod, customRange, isAllTime]);

  // Use core data hook
  const {
    coreLoading,
    coreError,
    overviewData,
    costBreakdown,
    setCostBreakdown,
    fetchCoreData,
    isMountedRef,
  } = useCoreData(queryString, requestKeySuffix, isAllTime);

  // Use secondary data hook
  const {
    secondaryLoading,
    secondaryError,
    templatePerformance,
    topPerformers,
    categoryAnalytics,
    modelUsageDistribution,
    categoryAnalyticsData,
    adapters,
    executionsPerWeek,
    providerDistribution,
    topModels,
    costComparison,
    fetchSecondaryData,
  } = useSecondaryData(
    queryString,
    requestKeySuffix,
    isAllTime,
    isMountedRef,
    setCostBreakdown,
    costBreakdown, // Pass costBreakdown from core data to avoid duplicate fetch
    templateGridPage,
    templateGridPageSize,
    templateGridCategory,
    templateGridHideZeroActivity,
    templateGridSearch,
    templateGridSortBy,
    templateGridSortOrder
  );

  // Main fetch function that orchestrates progressive loading
  const fetchDashboardData = useCallback(async () => {
    if (!isMountedRef.current) return;

    setLoading(true);
    setError(null);

    try {
      // First, load core data for immediate display
      await fetchCoreData();

      // Then load secondary data in background (don't await)
      fetchSecondaryData();
    } catch (err) {
      if (isMountedRef.current) {
        setError(
          `Failed to load dashboard data: ${err.message || 'Unknown error'}`
        );
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [fetchCoreData, fetchSecondaryData, isMountedRef]);

  // Load data on component mount and when dependencies change
  // Note: fetchDashboardData is intentionally excluded from dependencies to prevent infinite loops
  // It's recreated when fetchCoreData/fetchSecondaryData change, which is what we want
  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryString,
    requestKeySuffix,
    isAllTime,
    templateGridPage,
    templateGridPageSize,
    templateGridCategory,
    templateGridHideZeroActivity,
    templateGridSearch,
    templateGridSortBy,
    templateGridSortOrder,
  ]);

  // Reset mounted flag when component mounts
  useEffect(() => {
    isMountedRef.current = true;

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMountedRef.current = false;
    };
  }, [isMountedRef]);

  const refetch = useCallback(() => {
    if (isMountedRef.current) {
      fetchDashboardData();
    }
  }, [fetchDashboardData, isMountedRef]);

  const refetchSecondary = useCallback(() => {
    if (isMountedRef.current) {
      fetchSecondaryData();
    }
  }, [fetchSecondaryData, isMountedRef]);

  return {
    // Overall loading states
    loading,
    error,

    // Core data (loaded first)
    coreLoading,
    coreError,
    overviewData,
    costBreakdown,

    // Secondary data (loaded in background)
    secondaryLoading,
    secondaryError,
    templatePerformance,
    topPerformers,
    categoryAnalytics,
    modelUsageDistribution,
    categoryAnalyticsData,
    adapters,

    // New chart data
    executionsPerWeek,
    providerDistribution,
    topModels,
    costComparison,

    // Refetch functions
    refetch,
    refetchSecondary,
  };
};
